<script lang="ts" setup>
type Species = {
  title: string
  latinName: string
  family: string
  text: string
  portableTextSimple: string
  color: string
  status: string
  media: Record<string, unknown>
}

type Props = {
  title: string
  items: Species[]
}

const _props = defineProps<{ data: Props; theme: string }>()

const { isMobileOrTablet } = useDevice()
const { width: innerWidth } = useWindowSize()

const isMobile = computed(() => {
  if (import.meta.server) return isMobileOrTablet
  return innerWidth.value < 900
})

const activeIndex = ref(0)
const statuses = ['re', 'cr', 'en', 'vu', 'nt', 'dd', 'lc', 'na', 'ne']
</script>

<template>
  <div class="relative min-h-screen w-full py-7 md:py-8 lg:py-10 lg:text-(--white)">
    <div class="flex h-full w-full flex-col">
      <div
        class="mx-bleed relative z-[2] block border-t border-(--theme-fg)/15 lg:border-(--white)/15"
      />

      <div class="z-[2]">
        <CarouselsFeaturedSpecies
          :data="data"
          :index="activeIndex"
          @active-index="activeIndex = $event"
        />
      </div>
      <div v-if="isMobile" class="relative">
        <CarouselsFeaturedSpecieCard
          :data="data"
          :index="activeIndex"
          @active-index="activeIndex = $event"
        />
      </div>
      <div
        v-if="!isMobile"
        class="ml-bleed w-cols-4 relative z-[2] block rounded-sm lg:mt-24 lg:bg-(--white) lg:text-(--green-1)"
      >
        <TransitionGroup name="list" tag="ul" class="relative w-full">
          <li
            v-for="(item, idx) in data.items"
            v-show="activeIndex === idx"
            :key="item.title"
            class="w-full"
          >
            <div class="wrapper relative grid gap-5 lg:hidden">
              <client-only>
                <ElementsFeaturedSpeciesOverlay :data="item" class="relative" />
              </client-only>
            </div>
            <div class="wrapper hidden w-full justify-between p-6 lg:flex">
              <div class="content relative z-[2] flex flex-col justify-between gap-24">
                <div class="text-content flex flex-col gap-2">
                  <span class="font-sans text-xs italic opacity-50">{{ item.latinName }}</span>
                  <h3 class="font-display text-lg lg:text-xl">{{ item.title }}</h3>
                </div>
                <div class="flex flex-col gap-10 text-base">
                  <ElementsPortableTextWrapper
                    v-if="item.portableTextSimple"
                    :content="item.portableTextSimple"
                  />
                  <p v-else-if="item.text" class="text-base">{{ item.text }}</p>
                </div>
                <div class="flex flex-col gap-5 border-t pt-4 lg:border-(--green-1)/15">
                  <span class="uppercase">{{ $t('label.redlist') }}</span>
                  <ul class="flex justify-between">
                    <li
                      v-for="status in statuses"
                      :key="status"
                      class="flex h-8 w-8 items-center justify-center rounded-full border border-(--green-1)/15 text-sm"
                      :class="item.status == status ? 'bg-(--green-1) text-(--green-4)' : ''"
                    >
                      {{ status.toUpperCase() }}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </li>
        </TransitionGroup>
      </div>
      <TransitionGroup
        v-if="!isMobile"
        name="list"
        tag="ul"
        class="absolute top-0 left-0 hidden h-full w-full lg:block"
      >
        <li
          v-for="(item, idx) in data.items"
          v-show="activeIndex === idx"
          :key="item.title"
          class="h-full w-full"
        >
          <div class="overlay let-0 absolute top-0 h-full w-full bg-black opacity-25" />
          <div class="media-wrapper h-full w-full">
            <CommonMedia
              :media="item.media"
              class="h-full w-full"
              :sizes="'xs:100vw sm:100vw md:100vw lg:100vw xl:100vw xxl:100vw'"
            />
          </div>
        </li>
      </TransitionGroup>
    </div>
  </div>
</template>

<style scoped lang="postcss">
.embla {
  overflow: hidden;
  transition: background-color 0.5s ease-in-out;
}
.embla__container {
  display: flex;
  gap: 20px;
}
.embla__slide {
  --cols: 4;
  --gap: 20px;
  flex: 0 0 calc(var(--cols-4) - var(--grid-bleed));
  min-width: 0;
  @media (min-width: 900px) {
    flex: 0 0 calc((100% / var(--cols)) - (var(--gap) * (var(--cols) - 1) / var(--cols)));
  }
}

.list-move, /* apply transition to moving elements */
.list-enter-active,
.list-leave-active {
  transition: opacity 0.5s ease-in-out;
}

.list-enter-from {
  opacity: 0;
  /* transform: translateX(-30px); */
}

.list-leave-to {
  opacity: 0;
  /* transform: translateX(30px); */
}

/* ensure leaving items are taken out of layout flow so that moving
   animations can be calculated correctly. */
.list-leave-active {
  position: absolute;
  top: 0;
  width: 100%;
}
</style>
